import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle, BookOpen, Check, Edit3, FileText, Lightbulb, Map, RefreshCw, Sparkles, Target, Wand2
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import SpeechToTextButton from "../SpeechToTextButton";

export const ChapterPlanningModal = ({
  open,
  onOpenChange,
  chapterPlan,
  setChapterPlan,
  generatingChapter,
  project,
  projectId,
  API,
  onStartWriting,
  approveAndGenerateChapter,
}) => {
  return (
            <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Map className="w-5 h-5 text-blue-600" />
              Chapter {chapterPlan?.chapter_number} Plan
            </DialogTitle>
            <DialogDescription>
              Review and approve this plan before Shweebum writes the chapter
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {chapterPlan ? (
              <>
                {/* Chapter Overview */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-blue-800">{chapterPlan.chapter_title}</h4>
                    <p className="text-sm text-blue-700 mt-1">POV: {chapterPlan.pov_character}</p>
                    <p className="text-sm text-blue-600">Setting: {chapterPlan.setting} ({chapterPlan.time_of_day})</p>
                    <p className="text-xs text-blue-500 mt-2">~{chapterPlan.estimated_word_count} words | Story Progress: {chapterPlan.story_progress_percent}%</p>
                  </CardContent>
                </Card>

                {/* Chapter Goal */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-foreground mb-2">Chapter Goal</h4>
                    <p className="text-sm text-muted-foreground">{chapterPlan.chapter_goal}</p>
                  </CardContent>
                </Card>

                {/* Opening & Ending */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-3">
                      <h5 className="font-medium text-green-800 text-sm mb-1">Opening</h5>
                      <p className="text-xs text-green-700">{chapterPlan.opening_hook}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-cyan-500/25">
                    <CardContent className="p-3">
                      <h5 className="font-medium text-cyan-200 text-sm mb-1">Ending</h5>
                      <p className="text-xs text-cyan-300">{chapterPlan.chapter_ending}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Key Scenes */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-foreground mb-2">Key Scenes</h4>
                    <div className="space-y-2">
                      {chapterPlan.key_scenes?.map((scene, i) => (
                        <div key={i} className="bg-background p-2 rounded text-sm">
                          <p className="font-medium">{scene.scene}</p>
                          <p className="text-xs text-muted-foreground">Purpose: {scene.purpose}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Characters Appearing */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-foreground mb-2">Characters Appearing</h4>
                    <div className="flex flex-wrap gap-2">
                      {chapterPlan.characters_appearing?.map((char, i) => (
                        <Badge key={i} variant="outline">{char}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* New Elements Proposed */}
                {chapterPlan.new_elements_proposed?.length > 0 && (
                  <Card className="border-cyan-500/25 bg-slate-800/50">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-cyan-200 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> New Elements Proposed
                      </h4>
                      <p className="text-xs text-cyan-300 mb-2">These require your approval:</p>
                      {chapterPlan.new_elements_proposed.map((elem, i) => (
                        <div key={i} className="bg-card p-2 rounded mb-2 border border-cyan-500/25">
                          <p className="text-sm font-medium">{elem.name} ({elem.type})</p>
                          <p className="text-xs text-muted-foreground">Reason: {elem.reason}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Foreshadowing to Plant */}
                {chapterPlan.foreshadowing_to_plant?.length > 0 && (
                  <Card className="border-indigo-200 bg-indigo-50">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" /> Foreshadowing to Plant
                      </h4>
                      <p className="text-xs text-indigo-600 mb-3">These hints will be woven into this chapter for future reveals:</p>
                      <div className="space-y-2">
                        {chapterPlan.foreshadowing_to_plant.map((hint, i) => (
                          <div key={i} className="bg-card p-2 rounded border border-indigo-200">
                            <p className="text-xs text-indigo-700 font-medium">For: {hint.for_reveal}</p>
                            <p className="text-sm text-indigo-900">{hint.hint}</p>
                            <p className="text-xs text-indigo-500 mt-1">Type: {hint.type} | Integration: {hint.integration}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Will NOT Include */}
                {chapterPlan.will_NOT_include?.length > 0 && (
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-red-800 mb-2">Will NOT Include</h4>
                      <ul className="list-disc list-inside text-sm text-red-700">
                        {chapterPlan.will_NOT_include.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* User's Custom Plan - THIS OVERRIDES EVERYTHING */}
                <Card className="border-2 border-purple-300 bg-purple-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                      <Edit3 className="w-4 h-4" />
                      YOUR CUSTOM PLAN (This will be followed EXACTLY)
                    </h4>
                    <p className="text-xs text-purple-600 mb-2">
                      Write exactly what you want to happen. Include scenes, dialogue, character actions. The AI will follow this precisely.
                    </p>
                    <div className="relative">
                      <textarea 
                        className="w-full border-2 border-purple-200 rounded p-3 pr-10 text-sm bg-card focus:border-purple-400 focus:ring-2 focus:ring-purple-200 min-h-[300px] resize-y" 
                        rows={15}
                        defaultValue={chapterPlan?.suggested_custom_plan || ''}
                        placeholder="Write your detailed plan here. Example:

Era: 1958
Location: Rural Wisconsin
Characters: Heath, Sarah (pregnant wife)

1. Stormy night, Heath driving drunk with pregnant wife
2. Rock-and-roll music blasting, whiskey bottle under seat
3. Wife screaming in labor pains
4. Deer appears in road
5. Heath swerves, car flips down embankment
6. Heath ejected, wife trapped upside down
7. College boy witness calls police from payphone
8. Two officers (male and female) arrive
9. Female officer hears baby crying
10. She finds baby attached by umbilical cord
11. Male officer cuts cord with pocket knife
12. Female officer breaks down crying

Be as detailed as you want - the more specific, the better!"
                        id="chapter-custom-plan"
                      />
                      <SpeechToTextButton
                        onTranscript={(text, append) => {
                          const textarea = document.getElementById('chapter-custom-plan');
                          if (textarea) {
                            const currentValue = textarea.value || '';
                            textarea.value = append && currentValue ? currentValue + '\n' + text : text;
                            // Trigger input event to update any listeners
                            textarea.dispatchEvent(new Event('input', { bubbles: true }));
                          }
                        }}
                        className="absolute right-2 top-2 h-7 w-7"
                        size="icon"
                      />
                    </div>
                    <p className="text-xs text-purple-500 mt-1">
                      No limit - scroll to see all your text. The AI reads everything you write here.
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mb-2" />
                <p className="text-muted-foreground">Generating plan...</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { onOpenChange(false); setChapterPlan(null); }}>Cancel</Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                const customPlan = document.getElementById('chapter-custom-plan')?.value || '';
                if (approveAndGenerateChapter) {
                  approveAndGenerateChapter(customPlan);
                } else {
                  toast.error("Generation function not available");
                }
              }}
              disabled={!chapterPlan || generatingChapter !== null}
            >
              {generatingChapter ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Generating...</> : <>Generate Chapter</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


  );
};
